<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import {
  useCoreStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { AlternativeHotel } from '@apps/main/new-desktop/pages/tripOverview/models';
import { useTripOverviewPanelsStore } from '@apps/main/new-desktop/pages/tripOverview/stores';

import { AppButton, AppImg } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import AlternativeAccommodationCardContent from '../AlternativeAccommodationCardContent.vue';
import AccommodationPrice from '../AccommodationPrice.vue';
import DateCardTag from '../../DateCardTag.vue';

import MobileProductCardLayout from './MobileProductCardLayout.vue';

interface AlternativeHotelCardMobileProps {
  hotel: AlternativeHotel;
  ingredientIndex: number;
  date: string;
}

const props = defineProps<AlternativeHotelCardMobileProps>();

const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();

const isHideOverviewTimelineExperiment = computedAsync<boolean>(
  () => isExperimentAsync('hide_overview_timeline'),
  false
);

const isOverviewAccommodationCardFooterExperiment = computedAsync<boolean>(
  () => isExperimentAsync('overview_accommodation_card_footer'),
  false
);

function showDetails(shouldScrollToRooms: boolean): void {
  statsigEventsStore.isAlternativeHotelOpened = true;
  statsigEventsStore.logTripOverviewAlternativeHotelsCardClicked();
  tripOverviewPanelsStore.openHotelDetailsPanel(
    props.hotel.hotelCode,
    props.hotel.ingredientId,
    shouldScrollToRooms
  );
}
</script>

<template>
  <MobileProductCardLayout
    class="alternative-hotel-card-mobile"
    @click.stop="showDetails(false)"
  >
    <template #top-section>
      <AppImg
        class="image"
        :src="props.hotel.imgUrls[0] || ''"
        alt="alternative hotel image"
        @img-load-error="coreStore.handleImageLoadError"
      />
    </template>
    <template #content>
      <AlternativeAccommodationCardContent
        :hotel="props.hotel"
        :ingredient-index="props.ingredientIndex"
        @show-details="showDetails(false)"
        @show-rooms="showDetails(true)"
      />
    </template>
    <template #footer>
      <div class="footer">
        <AccommodationPrice :price="props.hotel.deltaPrice" />
        <div class="buttons">
          <AppButton
            :type="
              isOverviewAccommodationCardFooterExperiment
                ? 'primary'
                : 'secondary'
            "
            :icon="
              isOverviewAccommodationCardFooterExperiment
                ? { name: 'change' }
                : undefined
            "
            state="enabled"
            size="small"
            @click="
              showDetails(false),
                statsigEventsStore.logTripOverviewChangeAlternateHotelCardClicked()
            "
          >
            {{
              isOverviewAccommodationCardFooterExperiment
                ? $t('overview_accommodation_footer.change')
                : $t('planner.select')
            }}
          </AppButton>
        </div>
      </div>
    </template>
    <template v-if="isHideOverviewTimelineExperiment" #date>
      <DateCardTag :date="props.date" />
    </template>
  </MobileProductCardLayout>
</template>

<style scoped lang="scss">
.alternative-hotel-card-mobile {
  position: relative;

  .image {
    aspect-ratio: 4 / 3;
  }

  .content-container {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-16) var(--spacing-12);
    gap: var(--spacing-12);
    justify-content: space-between;
    height: 100%;

    .info-container {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .hotel-name {
        color: var(--color-black);
        @include font-label-l-bold;
        line-height: var(--line-heights-16);
        margin-bottom: var(--spacing-4);

        display: -webkit-box;
        max-width: var(--card-width);
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .polygon-name {
        @include font-label-s-light;
        color: var(--color-secondary-60);
      }
    }

    .rooms-container {
      padding: var(--spacing-16);
      background: var(--color-secondary-97);
      border: 1px solid var(--color-secondary-93);
      border-radius: var(--border-radius-8);
      margin: auto 0 0;

      .row {
        display: flex;
        gap: var(--spacing-4);
        margin-bottom: var(--spacing-8);

        .description {
          @include font-label-s-light;

          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          width: 0;
          min-width: 100%;

          .label {
            @include font-label-s-bold;
            white-space: nowrap;
          }

          &.single-room {
            white-space: normal;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            line-height: var(--line-heights-14);
          }
        }
      }
    }
  }

  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid var(--color-secondary-93);
    padding: 11px var(--spacing-12);

    .buttons {
      display: flex;
      align-items: center;
      gap: var(--spacing-12);
    }
  }
}
</style>
